/**
 * React Hook: useMeditationAudioUrl
 *
 * Converts relative audio paths to token-protected streaming URLs.
 * Handles both legacy full URLs and new relative paths.
 */

import { useState, useEffect } from 'react';
import {
  getMeditationAudioStreamingUrl,
  extractRelativePath,
  needsStreamingToken
} from '@/utils/audioStreaming';

interface UseMeditationAudioUrlResult {
  streamingUrl: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to convert audio URL to token-protected streaming URL
 *
 * @param audioUrl - Audio URL from session.audio_url (can be relative path or full URL)
 * @returns Object with streamingUrl, isLoading, and error states
 *
 * @example
 * const { streamingUrl, isLoading } = useMeditationAudioUrl(session.audio_url);
 * if (!isLoading && streamingUrl) {
 *   <audio src={streamingUrl} />
 * }
 */
export const useMeditationAudioUrl = (audioUrl: string | null | undefined): UseMeditationAudioUrlResult => {
  const [streamingUrl, setStreamingUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Reset state when audioUrl changes
    setStreamingUrl(null);
    setError(null);

    if (!audioUrl) {
      setIsLoading(false);
      return;
    }

    // If it's a blob URL (from audio generation), use it directly
    if (audioUrl.startsWith('blob:')) {
      setStreamingUrl(audioUrl);
      setIsLoading(false);
      return;
    }

    // If it's a shared session URL (already has /audio/shared/), use it directly
    if (audioUrl.includes('/audio/shared/')) {
      setStreamingUrl(audioUrl);
      setIsLoading(false);
      return;
    }

    // If it's a community session URL (already has /audio/community/), use it directly (no auth required)
    if (audioUrl.includes('/audio/community/')) {
      setStreamingUrl(audioUrl);
      setIsLoading(false);
      return;
    }

    // If it doesn't need a streaming token, use it directly
    if (!needsStreamingToken(audioUrl)) {
      setStreamingUrl(audioUrl);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch streaming URL with token
    const fetchStreamingUrl = async () => {
      setIsLoading(true);
      try {
        const relativePath = extractRelativePath(audioUrl);
        console.log(`🔐 Converting to streaming URL: ${relativePath}`);

        const url = await getMeditationAudioStreamingUrl(relativePath);
        setStreamingUrl(url);
        setError(null);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to get streaming URL');
        console.error('Error getting streaming URL:', error);
        setError(error);

        // SECURITY: Do not fallback to original URL - token-based access is mandatory
        // If token generation fails, the audio simply won't play
        setStreamingUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStreamingUrl();
  }, [audioUrl]);

  return { streamingUrl, isLoading, error };
};

/**
 * Synchronous helper to quickly check if URL needs conversion
 * Useful for conditional rendering before the hook processes
 */
export const shouldConvertToStreamingUrl = (audioUrl: string | null | undefined): boolean => {
  if (!audioUrl) return false;
  if (audioUrl.startsWith('blob:')) return false;
  if (audioUrl.includes('/audio/shared/')) return false;
  if (audioUrl.includes('/audio/community/')) return false;  // Community sessions are public
  return needsStreamingToken(audioUrl);
};
